/**
 * V6-010 砺锋台 V2 — localStorage 持久化 + 汇总
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §11.2 §21.2
 * @date 2026-07-19
 * @updated 2026-07-20 — 新增 PB/段位/aggregate 持久化
 */

import type { SharpenState, TypeProgress, OpenType, RankId, SharpenAggregate } from "./types";
import { DEFAULT_CODE_TEMPLATE, OPEN_TYPES, RANK_CONFIGS } from "./types";
import { dataProxy } from "../../core/data-proxy.js";

/** 创建初始 rankProgress */
function createInitialRankProgress(): Record<string, { cleared: number; total: number; battles: number }> {
  const rp: Record<string, { cleared: number; total: number; battles: number }> = {};
  for (const rc of RANK_CONFIGS) {
    rp[rc.id] = { cleared: 0, total: rc.floorCount, battles: 0 };
  }
  return rp;
}

/** 创建初始 TypeProgress（含段位+PB 字段） */
export function createInitialProgress(): TypeProgress {
  return {
    exp: 0,
    totalBattles: 0,
    wins: 0,
    losses: 0,
    clearedLevels: [],
    failedLevels: [],
    recentBattles: [],
    code: DEFAULT_CODE_TEMPLATE,
    currentRank: "soldier" as RankId,
    rankProgress: createInitialRankProgress() as TypeProgress["rankProgress"],
    pbMap: {},
    personalBests: { longestStreak: 0, currentStreak: 0 },
  };
}

/** 计算全类型汇总 */
export function computeAggregate(state: SharpenState): SharpenAggregate {
  let totalBattles = 0;
  let totalWins = 0;
  let totalExp = 0;
  let longestStreakAll = 0;

  for (const t of OPEN_TYPES) {
    const p = state.typeProgress[t];
    totalBattles += p.totalBattles;
    totalWins += p.wins;
    totalExp += p.exp;
    longestStreakAll = Math.max(longestStreakAll, p.personalBests.longestStreak);
  }

  const avgWinRate = totalBattles > 0 ? totalWins / totalBattles : 0;

  return { totalBattles, totalWins, totalExp, longestStreakAllTypes: longestStreakAll, avgWinRate };
}

/** 创建初始 SharpenState */
export function createInitialState(): SharpenState {
  const state: SharpenState = {
    typeProgress: {
      "1v1": createInitialProgress(),
      "2v2": createInitialProgress(),
      "3v3": createInitialProgress(),
    },
    lastSelectedType: "1v1",
    aggregate: { totalBattles: 0, totalWins: 0, totalExp: 0, longestStreakAllTypes: 0, avgWinRate: 0 },
    _version: 2,
  };
  return state;
}

/** 从 DataProxy 加载状态（兼容旧版本无 aggregate/rank/pb 字段） */
export function loadState(): SharpenState {
  try {
    const parsed = dataProxy.get<Partial<SharpenState> & { _version?: number }>("sharpen-v3");
    if (!parsed) return createInitialState();

    // V6-011: 旧版本数据（mock ID 如 c001）与真实关卡 ID 不兼容，清空重来
    if (!parsed._version || parsed._version < 2) {
      console.warn("[sharpen] 检测到旧版本数据（V6-011 前），已清空重置");
      dataProxy.remove("sharpen-v3");
      return createInitialState();
    }

    const state = createInitialState();
    if (parsed.typeProgress) {
      for (const t of OPEN_TYPES) {
        if (parsed.typeProgress[t]) {
          const saved = parsed.typeProgress[t];
          const init = createInitialProgress();
          state.typeProgress[t] = {
            ...init,
            ...saved,
            // 兼容旧版本无 rank/pb/failedLevels 字段
            currentRank: saved.currentRank || init.currentRank,
            rankProgress: saved.rankProgress || init.rankProgress,
            pbMap: saved.pbMap || init.pbMap,
            personalBests: saved.personalBests || init.personalBests,
            failedLevels: saved.failedLevels || init.failedLevels,
          };
        }
      }
    }
    if (parsed.lastSelectedType && OPEN_TYPES.includes(parsed.lastSelectedType)) {
      state.lastSelectedType = parsed.lastSelectedType;
    }
    // 重新计算 aggregate（不信任旧数据）
    state.aggregate = computeAggregate(state);
    return state;
  } catch {
    return createInitialState();
  }
}

/** 保存状态到 DataProxy（自动重新计算 aggregate） */
export function saveState(state: SharpenState): void {
  try {
    const toSave = { ...state, aggregate: computeAggregate(state) };
    dataProxy.set("sharpen-v3", toSave);
  } catch (e) {
    console.warn("[sharpen-v3] save state failed:", e);
  }
}

/** 重置状态（清空 DataProxy + 返回初始状态） */
export function resetState(): SharpenState {
  try {
    dataProxy.remove("sharpen-v3");
  } catch (e) {
    console.warn("[sharpen-v3] reset state failed:", e);
  }
  return createInitialState();
}

/** 更新某类型进度（immutable 风格，自动重算 aggregate） */
export function updateTypeProgress(
  state: SharpenState,
  type: OpenType,
  updater: (p: TypeProgress) => TypeProgress
): SharpenState {
  const newState = {
    ...state,
    typeProgress: {
      ...state.typeProgress,
      [type]: updater(state.typeProgress[type]),
    },
  };
  newState.aggregate = computeAggregate(newState);
  return newState;
}

/** 设置最后选中类型 */
export function setLastSelectedType(
  state: SharpenState,
  type: OpenType
): SharpenState {
  return { ...state, lastSelectedType: type };
}
